import { publicRequest } from '@/lib/axios/public-request';
import type {
  BlogPost,
  BlogPostsPayload,
  BlogPostsRequest,
  BlogTag,
  BlogTagsPayload,
  BlogTagsRequest,
} from '@/types/blog';
import type { ApiResponse } from '@/types/user';
import { integrationService } from '@/utils/api-service-controllers-route';
import { BASE_URL } from '@/utils/api-urls';
import {
  BLOG_AGENT_KEY,
  BLOG_TENANT_ID,
  BLOG_WORDPRESS_SITE_IDS,
} from '@/utils/blog-config';

type BlogPostsDataEnvelope = {
  data?: unknown;
};

type UnknownBlogPostsData =
  | BlogPost[]
  | BlogPostsPayload
  | BlogPostsPayload[]
  | BlogPostsDataEnvelope
  | string
  | null;

type UnknownBlogTagsData =
  | BlogTag[]
  | BlogTagsPayload
  | BlogTagsPayload[]
  | BlogPostsDataEnvelope
  | string
  | null;

const BLOG_POSTS_PATH = `${integrationService}/blog/posts`;
const BLOG_TAGS_PATH = `${integrationService}/blog/tags`;

const parseMaybeJson = <T>(value: string): T | string => {
  try {
    return JSON.parse(value) as T;
  } catch {
    return value;
  }
};

const isObject = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object';

const isBlogPost = (value: unknown): value is BlogPost =>
  isObject(value) && typeof value.ID === 'number';

const isBlogTag = (value: unknown): value is BlogTag =>
  isObject(value) && typeof value.name === 'string';

const normalizeParsedPostsPayload = (parsed: unknown): BlogPostsPayload => {
  if (Array.isArray(parsed)) {
    const nestedPayload = parsed.find(
      item => isObject(item) && Array.isArray(item.posts)
    ) as BlogPostsPayload | undefined;

    if (nestedPayload) return nestedPayload;

    const posts = parsed.filter(isBlogPost);
    return { posts };
  }

  if (isObject(parsed)) {
    if (Array.isArray(parsed.posts)) {
      const posts = parsed.posts.filter(isBlogPost);
      return { ...parsed, posts };
    }

    if ('data' in parsed) {
      return normalizeParsedPostsPayload(parsed.data);
    }
  }

  return { posts: [] };
};

const normalizeParsedTagsPayload = (parsed: unknown): BlogTagsPayload => {
  if (Array.isArray(parsed)) {
    const nestedPayload = parsed.find(
      item => isObject(item) && Array.isArray(item.tags)
    ) as BlogTagsPayload | undefined;

    if (nestedPayload) return nestedPayload;

    const tags = parsed.filter(isBlogTag);
    return { tags };
  }

  if (isObject(parsed)) {
    if (Array.isArray(parsed.tags)) {
      const tags = parsed.tags.filter(isBlogTag);
      return { ...parsed, tags };
    }

    if ('data' in parsed) {
      return normalizeParsedTagsPayload(parsed.data);
    }
  }

  return { tags: [] };
};

const normalizePostsPayload = (
  payload: UnknownBlogPostsData
): BlogPostsPayload => {
  const parsed =
    typeof payload === 'string' ? parseMaybeJson(payload) : payload;

  return normalizeParsedPostsPayload(parsed);
};

const normalizeTagsPayload = (
  payload: UnknownBlogTagsData
): BlogTagsPayload => {
  const parsed =
    typeof payload === 'string' ? parseMaybeJson(payload) : payload;

  return normalizeParsedTagsPayload(parsed);
};

class BlogService {
  private readonly client = publicRequest(BASE_URL);

  async getPosts({
    variant,
    page = 1,
    perPage = 10,
    status,
    tag,
  }: BlogPostsRequest) {
    const response = await this.client.get<ApiResponse<UnknownBlogPostsData>>(
      BLOG_POSTS_PATH,
      {
        headers: {
          'X-WordPress-Site-Id': BLOG_WORDPRESS_SITE_IDS[variant],
        },
        params: {
          tenantId: BLOG_TENANT_ID,
          agentKey: BLOG_AGENT_KEY,
          page,
          perPage,
          ...(status ? { status } : {}),
          ...(tag ? { tag } : {}),
        },
      }
    );

    return normalizePostsPayload(response.data.data);
  }

  async getTags({ variant, page = 1, perPage = 50 }: BlogTagsRequest) {
    const response = await this.client.get<ApiResponse<UnknownBlogTagsData>>(
      BLOG_TAGS_PATH,
      {
        headers: {
          'X-WordPress-Site-Id': BLOG_WORDPRESS_SITE_IDS[variant],
        },
        params: {
          tenantId: BLOG_TENANT_ID,
          agentKey: BLOG_AGENT_KEY,
          page,
          perPage,
        },
      }
    );

    return normalizeTagsPayload(response.data.data);
  }

  async getPost(postId: number | string, variant: BlogPostsRequest['variant']) {
    const response = await this.client.get<ApiResponse<BlogPost>>(
      `${BLOG_POSTS_PATH}/${postId}`,
      {
        headers: {
          'X-WordPress-Site-Id': BLOG_WORDPRESS_SITE_IDS[variant],
        },
        params: {
          tenantId: BLOG_TENANT_ID,
          agentKey: BLOG_AGENT_KEY,
        },
      }
    );

    return response.data.data;
  }
}

export const blogService = new BlogService();
